
import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useRememberedState, useRememberedScroll } from '../hooks/useRemembered';
import { UserProfile, ReportMetadata } from '../types';
import { Map as MapIcon, Search, MapPin, MapPinOff, Navigation, ArrowRight, Building2, Globe, LayoutGrid, Loader2, Layers, X, Home, Calendar, MousePointer2, Users, Check, Route as RouteIcon, Maximize2, Minimize2 } from 'lucide-react';
import { Button } from './Button';
import { supabase } from '../services/supabaseClient';
import { istUebungsprojekt } from '../services/uebung/uebungService';
import { hatNochKeineBegehung } from '../services/projectType';
import { isProjectFinished } from '../services/projectBilling';
import { heuteBerlin } from '../services/dateService';
import { externOeffnen } from '../services/externOeffnen';
import { useRechte } from '../hooks/useRechte';
import { selectMapAllProjects, type ScopeUser } from '../hooks/useProjectData';
import {
    begehungstage,
    buendeln,
    distanzKm,
    faelligkeit,
    geocodeMitCache,
    googleMapsLinks,
    istMeineBegehung,
    ordneTour,
    type Faelligkeit,
    type GeoPunkt,
} from '../services/tourPlanning';
import L from 'leaflet';
import type { ListProject } from '../services/listProject';

interface Props {
    projects: ListProject[]; // Cloud projects
    userProfile: UserProfile | null;
    onOpenProject: (project: ListProject) => void;
    initialSearchTerm?: string;
    onRefresh?: () => void;
}

/** HTML-Text sicher einbetten. Tooltip und Popup sind rohes HTML — hier kommen Namen rein. */
const esc = (s: unknown): string =>
    String(s ?? '').replace(/[&<>"']/g, (c) =>
        ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c] as string,
    );

// Fallback color generator based on User ID
const getFallbackColor = (userId: string) => {
    if (!userId) return '#64748b';
    const colors = [
        '#ef4444', '#f97316', '#f59e0b', '#84cc16', '#10b981', '#06b6d4',
        '#0ea5e9', '#3b82f6', '#6366f1', '#a855f7', '#d946ef', '#ec4899', '#f43f5e'
    ];
    let hash = 0;
    for (let i = 0; i < userId.length; i++) {
        hash = userId.charCodeAt(i) + ((hash << 5) - hash);
    }
    return colors[Math.abs(hash) % colors.length];
};

interface OwnerInfo {
    name: string;
    color?: string;
}

/** Ein fest verorteter Punkt mit lesbarer Adresse (Firma, Zuhause, Suche). */
type Ort = GeoPunkt & { address: string };

/** Wo die Tagestour beginnt. Fehlt der gewaehlte Ort, gilt der naechste vorhandene. */
type RouteStart = 'standort' | 'firma' | 'zuhause';

/**
 * Ein Listeneintrag in Liste und Karte: Koordinaten aus
 * `globalData.locationCoordinates` (fehlen sie, steht `undefined` drin),
 * Entfernung nur bei einer Ortssuche.
 */
type KartenProjekt = ListProject & { lat: number; lng: number; distance: number | null };

const WEEKDAYS = [
    { id: 1, label: 'Mo', long: 'Montag' },
    { id: 2, label: 'Di', long: 'Dienstag' },
    { id: 3, label: 'Mi', long: 'Mittwoch' },
    { id: 4, label: 'Do', long: 'Donnerstag' },
    { id: 5, label: 'Fr', long: 'Freitag' },
    { id: 6, label: 'Sa', long: 'Samstag' },
    { id: 0, label: 'So', long: 'Sonntag' },
];

// Marker-Buendel: nur in der Wochenansicht (eine Tagestour braucht jeden
// Stopp einzeln), nur weit herausgezoomt und erst ab vielen Markern —
// „Meine" mit einer Handvoll Projekte bleibt wie bisher.
const BUENDEL_BIS_ZOOM = 13;
const BUENDEL_AB_MARKERN = 30;
const BUENDEL_ZELLE_PX = 60;

const kurzDatum = (iso: string) => {
    const [, m, d] = iso.split('-');
    return `${d}.${m}.`;
};

const faelligText = (f: Faelligkeit): string => {
    if (f.tage < 0) return `überfällig seit ${-f.tage} ${f.tage === -1 ? 'Tag' : 'Tagen'}`;
    if (f.tage === 0) return 'heute fällig';
    if (f.tage === 1) return 'morgen fällig';
    return `fällig ${kurzDatum(f.datum)}`;
};

const faelligKlasse = (f: Faelligkeit): string => {
    if (f.tage < 0) return 'bg-red-100 text-red-700';
    if (f.tage <= 1) return 'bg-amber-100 text-amber-800';
    return 'bg-slate-100 text-slate-600';
};

export const GlobalMapDashboard: React.FC<Props> = ({ projects: alleProjekte, userProfile, onOpenProject, initialSearchTerm }) => {
    // ÜBUNGSPROJEKTE FALLEN IN KEINE AUSWERTUNG (docs/lernzentrum-tutorial-plan.md,
    // 1.11 und 8.2). Gefiltert wird hier und nicht beim Laden, weil die Liste als
    // Prop ankommt und dieselbe Liste zugleich die Projektverwaltung speist, die
    // Übungsprojekte sehr wohl zeigt.
    const projects = useMemo(() => alleProjekte.filter(p => !istUebungsprojekt(p)), [alleProjekte]);

    const [searchTerm, setSearchTerm] = useRememberedState('map.search', initialSearchTerm || '');
    const listScrollRef = useRememberedScroll('map.list.scroll');
    // Gemerkt wird nur die id. Das Projekt selbst kommt aus der angezeigten
    // Liste — nur dort traegt es seine Koordinaten (lat/lng); das rohe Projekt
    // hat keine, und Detailansicht wie Zoom liefen damit ins Leere.
    const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);

    // Day Filter (controlled by Sidebar Headers)
    const [selectedDay, setSelectedDay] = useRememberedState<number | 'all'>('map.day', 'all');

    const [showOnlyMine, setShowOnlyMine] = useRememberedState('map.onlyMine', true);
    // Beendet und Archiv: die Arbeit vor Ort ist vorbei, das Projekt gehoert
    // nicht mehr auf die Tour. Abschaltbar, um alte Baustellen nachzuschlagen.
    const [hideFinished, setHideFinished] = useRememberedState('map.hideFinished', true);
    // Stand V0: noch keine Begehung durchgefuehrt (services/projectType.ts).
    const [onlyNoInspection, setOnlyNoInspection] = useRememberedState('map.onlyNoInspection', false);
    const [routeStart, setRouteStart] = useRememberedState<RouteStart>('map.routeStart', 'standort');
    const [userLocation, setUserLocation] = useState<GeoPunkt | null>(null);

    // Admin User Filter: gemerkt werden die AUSGEBLENDETEN Besitzer. Leer =
    // alle sichtbar; neue Besitzer nach einem Neuladen erscheinen von selbst,
    // und eine getroffene Auswahl ueberlebt das Neuladen. Schluessel ist
    // `user_id || ''` — auch Projekte ohne Besitzer lassen sich ausblenden.
    const [hiddenOwnerIds, setHiddenOwnerIds] = useState<Set<string>>(new Set());
    const [isOwnerFilterOpen, setIsOwnerFilterOpen] = useState(false);

    // Special Locations (Geocoded on mount)
    const [homeLocation, setHomeLocation] = useState<Ort | null>(null);
    const [companyLocation, setCompanyLocation] = useState<Ort | null>(null);

    const [viewMode, setViewMode] = useRememberedState<'single' | 'all'>('map.viewMode', 'all');

    const [searchLocation, setSearchLocation] = useState<Ort | null>(null);
    const [isSearchingLocation, setIsSearchingLocation] = useState(false);

    const [mapLayer, setMapLayer] = useRememberedState<'roadmap' | 'satellite'>('map.layer', 'roadmap');
    // Karte im Vollbild — auf dem Handy bleibt neben Kopf und Liste sonst nur ein Streifen.
    const [isMapFullscreen, setIsMapFullscreen] = useState(false);
    const [projectOwners, setProjectOwners] = useState<Record<string, OwnerInfo>>({});

    const mapContainerRef = useRef<HTMLDivElement>(null);
    const mapInstanceRef = useRef<L.Map | null>(null);
    const markersRef = useRef<L.Marker[]>([]);
    const routeLineRef = useRef<L.Polyline | null>(null);
    const tileLayerRef = useRef<L.TileLayer | null>(null);
    // Immer die Zeichenfunktion des letzten Renders — der zoomend-Handler der
    // Karte haengt sonst an den Daten vom Moment ihrer Erzeugung.
    const renderMarkersRef = useRef<(fit: boolean) => void>(() => {});
    // Zoomstufe der letzten Zeichnung — oberhalb der Buendelgrenze aendert ein
    // weiterer Zoom nichts, dann bleibt alles stehen (offene Popups auch).
    const markerZoomRef = useRef<number | null>(null);
    // Welcher Marker zu welchem Projekt gehoert — fuer das offene Popup beim Neuzeichnen.
    const markerProjectIdsRef = useRef(new Map<L.Marker, string>());

    // Schalter „Meine/Alle": jeder mit dem Recht `projekte.alle_sehen`
    // (Rollensystem; ohne Rechte aus der Datenbank wie bisher admin,
    // super_admin und Arbeitsvorbereitung). Vorgabe bleibt „Meine".
    const rechte = useRechte(userProfile?.role);
    const darfAlle = rechte.hat('projekte.alle_sehen');
    // Firmenzuordnung der Projektbesitzer — für „Alle" ohne Admin-Rolle
    // (selectMapAllProjects). Kommt mit dem Abruf der Besitzer unten.
    const [ownerScope, setOwnerScope] = useState<ScopeUser[] | null>(null);

    // Initial Search Effect
    useEffect(() => {
        if (initialSearchTerm) {
            setSearchTerm(initialSearchTerm);
            performGlobalSearch(initialSearchTerm);
        }
    }, [initialSearchTerm]);

    // Fetch Owners
    useEffect(() => {
        const fetchOwners = async () => {
            const userIds = Array.from(new Set(projects.map(p => p.user_id))).filter(Boolean);
            if (userIds.length === 0) return;
            // Mit Firmenzuordnung fuer „Alle"; fehlt `company_id` (companies.sql
            // #9 offen, 42703), traegt der Name allein — wie fetchScopeUsers in
            // ProjectOverview.
            const spalten = 'id, first_name, last_name, settings';
            const mitFirma = await supabase.from('profiles').select(`${spalten}, company_id, company_name`).in('id', userIds);
            const { data } = mitFirma.error
                ? await supabase.from('profiles').select(`${spalten}, company_name`).in('id', userIds)
                : mitFirma;
            if (data) {
                const map: Record<string, OwnerInfo> = {};
                const scope: ScopeUser[] = [];
                data.forEach((u: any) => {
                    const name = `${u.first_name || ''} ${u.last_name || ''}`.trim();
                    const color = u.settings?.mapColor;
                    // Auch ohne Namen: die Kartenfarbe gilt trotzdem; der Name
                    // faellt in Liste und Filter auf den Koordinator zurueck.
                    map[u.id] = { name, color };
                    scope.push({ id: u.id, company_id: u.company_id ?? null, company_name: u.company_name ?? null });
                });
                setProjectOwners(map);
                setOwnerScope(scope);
            }
        };
        fetchOwners();
    }, [projects]);

    // Die Projekte hinter „Alle" (useProjectData.selectMapAllProjects).
    // `null` = es gilt „Meine": so gewaehlt, ohne Recht, oder der
    // Firmenbereich ist noch nicht bestimmbar (Besitzer laden noch).
    const alleProjekteKarte = useMemo(
        () => (showOnlyMine ? null : selectMapAllProjects(projects, userProfile, rechte, ownerScope)),
        [projects, userProfile, rechte, ownerScope, showOnlyMine],
    );
    const zeigeAlle = alleProjekteKarte !== null;
    const alleLaedt = darfAlle && !showOnlyMine && !zeigeAlle;

    // Auswahlliste des Benutzerfilters: JEDER Besitzer, der unter „Alle" ein
    // Projekt traegt — auch ohne Vor- und Nachnamen im Profil (dann
    // Koordinator aus dem Projekt).
    const ownerOptions = useMemo(() => {
        const fallbackNames = new Map<string, string>();
        (alleProjekteKarte ?? []).forEach(p => {
            const id = p.user_id || '';
            if (!fallbackNames.get(id)) fallbackNames.set(id, p.data?.metadata?.coordinator || '');
        });
        return Array.from(fallbackNames, ([id, fallback]) => ({
            id,
            name: projectOwners[id]?.name || fallback || (id ? 'Unbekannt' : 'Ohne Besitzer'),
            color: projectOwners[id]?.color || getFallbackColor(id),
        })).sort((a, b) => a.name.localeCompare(b.name));
    }, [alleProjekteKarte, projectOwners]);

    // Geocode Home & Company Address (im Browser zwischengespeichert, services/tourPlanning.ts)
    useEffect(() => {
        const verorte = (address: string | undefined, setzen: (ort: Ort) => void, art: string) => {
            if (!address) return;
            geocodeMitCache(address)
                .then(ort => { if (ort) setzen(ort); })
                .catch(() => console.error("Geocoding failed for " + art));
        };
        verorte(userProfile?.company_address, setCompanyLocation, 'company');
        verorte(userProfile?.address, setHomeLocation, 'home');
    }, [userProfile]);

    // Browser Geolocation
    useEffect(() => {
        if ("geolocation" in navigator) {
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    setUserLocation({
                        lat: position.coords.latitude,
                        lng: position.coords.longitude
                    });
                },
                (error) => console.log("Geo error:", error)
            );
        }
    }, []);

    // 1. Ownership Filter
    const ownedProjects = useMemo(() => {
        if (!alleProjekteKarte) {
            // Eigenes Projekt, Ausfuehrungs-Verantwortlicher aus der
            // Vertreterliste oder aktive Urlaubsvertretung. Wer nur
            // Verantwortliche Planungsphase ist, faehrt nicht zur Begehung
            // und gehoert nicht auf die eigene Tour (services/tourPlanning.ts).
            return projects.filter(p => istMeineBegehung(p, userProfile));
        }
        // „ALLE": ausgeblendete Besitzer weglassen
        if (hiddenOwnerIds.size === 0) return alleProjekteKarte;
        return alleProjekteKarte.filter(p => !hiddenOwnerIds.has(p.user_id || ''));
    }, [projects, userProfile, alleProjekteKarte, hiddenOwnerIds]);

    // 2. Status Filter: Beendet + Archiv (Spalte ODER Dokument, services/projectBilling.ts)
    //    und Begehungsstand. Gezaehlt wird mit, damit die Liste sagen kann,
    //    was gerade fehlt.
    const statusFilter = useMemo(() => {
        let list = ownedProjects;
        let beendet = 0;
        let mitBegehung = 0;
        if (hideFinished) {
            const rest = list.filter(p => !isProjectFinished(p));
            beendet = list.length - rest.length;
            list = rest;
        }
        if (onlyNoInspection) {
            const rest = list.filter(p => hatNochKeineBegehung(p));
            mitBegehung = list.length - rest.length;
            list = rest;
        }
        return { list, beendet, mitBegehung };
    }, [ownedProjects, hideFinished, onlyNoInspection]);

    // 3. Suche, Sortierung, Koordinaten (For List and Map)
    const displayedProjects = useMemo((): KartenProjekt[] => {
        let list = statusFilter.list;

        if (searchLocation) {
            const withDistance = list.map(p => {
                const meta = p.data.metadata as ReportMetadata;
                const coords = meta?.globalData?.locationCoordinates;
                let distance = Infinity;
                if (coords?.lat && coords?.lng) {
                    distance = distanzKm(searchLocation, coords);
                }
                return { ...p, lat: coords?.lat, lng: coords?.lng, distance };
            });
            const valid = withDistance.filter(p => p.distance !== Infinity);
            valid.sort((a, b) => a.distance - b.distance);
            return valid.slice(0, 5);
        }

        if (searchTerm) {
            const lower = searchTerm.toLowerCase();
            list = list.filter(p =>
                (p.name || '').toLowerCase().includes(lower) ||
                (p.data.metadata?.projectAddress || '').toLowerCase().includes(lower) ||
                (p.data.metadata?.projectNumber || '').toLowerCase().includes(lower)
            );
        }

        // SORTING: unter „Alle" nach Koordinator
        // (Kopie: ohne vorherigen Filter ist `list` noch das Prop-Array.)
        if (zeigeAlle) {
            list = [...list].sort((a, b) => {
                const nameA = projectOwners[a.user_id]?.name || 'Z_Unknown';
                const nameB = projectOwners[b.user_id]?.name || 'Z_Unknown';
                return nameA.localeCompare(nameB);
            });
        }

        // Return standard structure
        return list.map(p => {
            const meta = p.data.metadata as ReportMetadata;
            const coords = meta?.globalData?.locationCoordinates;
            return {
                ...p,
                lat: coords?.lat,
                lng: coords?.lng,
                distance: null
            };
        });

    }, [statusFilter, searchTerm, zeigeAlle, projectOwners, searchLocation]);

    const selectedProject = useMemo(
        () => displayedProjects.find(p => p.id === selectedProjectId) || null,
        [displayedProjects, selectedProjectId],
    );

    // EINE Tag-Regel fuer Liste, Karte und Route (services/tourPlanning.ts).
    const tageById = useMemo(
        () => new Map<string, number[]>(displayedProjects.map(p => [p.id, begehungstage(p)])),
        [displayedProjects],
    );
    const faelligById = useMemo(() => {
        const heute = heuteBerlin();
        return new Map<string, Faelligkeit | null>(displayedProjects.map(p => [p.id, faelligkeit(p, heute)]));
    }, [displayedProjects]);

    const istGedimmt = (p: { id: string }) => selectedDay !== 'all' && !(tageById.get(p.id) || []).includes(selectedDay);

    // Grouping Logic for Sidebar
    const groupedProjects = useMemo(() => {
        const groups: Record<number | string, KartenProjekt[]> = {};

        // Init groups
        [1, 2, 3, 4, 5, 6, 0].forEach(d => groups[d] = []);
        groups['none'] = [];

        displayedProjects.forEach(p => {
            const tage = tageById.get(p.id) || [];
            if (tage.length === 0) groups['none'].push(p);
            else tage.forEach(t => groups[t].push(p));
        });

        return groups;
    }, [displayedProjects, tageById]);

    // --- TAGESTOUR ---
    const startOptionen = useMemo(() => [
        { id: 'standort' as RouteStart, label: 'Aktueller Standort', ort: userLocation as GeoPunkt | null },
        { id: 'firma' as RouteStart, label: 'Firma', ort: companyLocation as GeoPunkt | null },
        { id: 'zuhause' as RouteStart, label: 'Zuhause', ort: homeLocation as GeoPunkt | null },
    ].filter(o => o.ort !== null), [userLocation, companyLocation, homeLocation]);
    const aktiverStart = startOptionen.find(o => o.id === routeStart) || startOptionen[0] || null;
    const tourStart: GeoPunkt | null = aktiverStart?.ort ?? null;

    const tour = useMemo(() => {
        if (selectedDay === 'all') return null;
        const stopps = displayedProjects.filter(p => p.lat && p.lng && (tageById.get(p.id) || []).includes(selectedDay));
        return ordneTour(tourStart, stopps);
    }, [selectedDay, displayedProjects, tageById, tourStart]);

    const tourNummer = useMemo(
        () => new Map<string, number>((tour?.stopps || []).map((p, i) => [p.id, i + 1])),
        [tour],
    );
    const tourLinks = useMemo(
        () => (tour && tour.stopps.length > 0 ? googleMapsLinks([...(tourStart ? [tourStart] : []), ...tour.stopps]) : []),
        [tour, tourStart],
    );

    const ohneKoordinaten = displayedProjects.filter(p => !p.lat || !p.lng).length;
    const buendelMoeglich = selectedDay === 'all' && displayedProjects.length - ohneKoordinaten > BUENDEL_AB_MARKERN;
    const ausgeblendet: string[] = [];
    if (statusFilter.beendet > 0) ausgeblendet.push(`${statusFilter.beendet} beendet`);
    if (statusFilter.mitBegehung > 0) ausgeblendet.push(`${statusFilter.mitBegehung} durch „Nur ohne Begehung“`);

    const openProjectById = (id: string) => {
        const fullProject = projects.find(proj => proj.id === id);
        if (fullProject) onOpenProject(fullProject);
    };

    // Search Execution
    const performGlobalSearch = async (termOverride?: string) => {
        const term = termOverride || searchTerm;
        if (!term.trim()) return;
        setIsSearchingLocation(true);
        try {
            const url = `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(term)}&limit=1`;
            const res = await fetch(url, { headers: { 'User-Agent': 'SiGeKo-App' } });
            const data = await res.json();
            if (data && data.length > 0) {
                setSearchLocation({ lat: parseFloat(data[0].lat), lng: parseFloat(data[0].lon), address: data[0].display_name });
                setViewMode('all');
            } else {
                if (!termOverride) alert("Ort nicht gefunden.");
                setSearchLocation(null);
            }
        } catch { setSearchLocation(null); }
        finally { setIsSearchingLocation(false); }
    };

    // Vor allen Karten-Effekten: die Zeichenfunktion auf den neuesten Stand.
    useEffect(() => {
        renderMarkersRef.current = (fit: boolean) => {
            if (viewMode !== 'all' || !mapInstanceRef.current) return;
            // Ohne Buendel aendert ein Zoom nichts an den Markern.
            if (!fit && !buendelMoeglich) return;
            updateMapMarkers(mapInstanceRef.current, fit);
        };
    });

    // --- MAP INITIALIZATION ---
    useEffect(() => {
        if (viewMode !== 'all' || !mapContainerRef.current) return;

        // Cleanup
        if (mapInstanceRef.current) {
            mapInstanceRef.current.remove();
            mapInstanceRef.current = null;
            markersRef.current = [];
        }

        const map = L.map(mapContainerRef.current, { zoomControl: false, attributionControl: false }).setView([51.1657, 10.4515], 6);
        L.control.zoom({ position: 'bottomright' }).addTo(map);
        mapInstanceRef.current = map;
        // Buendel haengen am Zoom: nach jedem Zoomen neu einteilen, ohne die
        // Ansicht zu verschieben (fit = false). Erst im naechsten Takt: bei
        // grossen Zoomspruengen feuert Leaflet `zoomend` SYNCHRON mitten aus
        // fitBounds/setView — das Neuzeichnen liefe sonst in das laufende hinein.
        map.on('zoomend', () => {
            setTimeout(() => {
                if (mapInstanceRef.current !== map) return;
                const before = markerZoomRef.current;
                if (before !== null && before >= BUENDEL_BIS_ZOOM && map.getZoom() >= BUENDEL_BIS_ZOOM) return;
                renderMarkersRef.current(false);
            }, 0);
        });

        setTimeout(() => {
            map.invalidateSize();
        }, 100);

        updateTileLayer(map, mapLayer);
        updateMapMarkers(map, true);

        return () => {
            if (mapInstanceRef.current) {
                mapInstanceRef.current.remove();
                mapInstanceRef.current = null;
            }
            markersRef.current = [];
            routeLineRef.current = null;
            markerZoomRef.current = null;
        };
    }, [viewMode]);

    // Vollbild an/aus: Leaflet muss die neue Groesse kennen; Escape verlaesst es.
    useEffect(() => {
        const timer = setTimeout(() => mapInstanceRef.current?.invalidateSize(), 50);
        if (!isMapFullscreen) return () => clearTimeout(timer);
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setIsMapFullscreen(false); };
        window.addEventListener('keydown', onKey);
        return () => { clearTimeout(timer); window.removeEventListener('keydown', onKey); };
    }, [isMapFullscreen]);

    // Layer Update
    useEffect(() => {
        if (mapInstanceRef.current) updateTileLayer(mapInstanceRef.current, mapLayer);
    }, [mapLayer]);

    // Marker Update
    useEffect(() => {
        renderMarkersRef.current(true);
    }, [displayedProjects, searchLocation, userLocation, homeLocation, companyLocation, selectedDay, selectedProject, tour, faelligById]);

    const updateTileLayer = (map: L.Map, layer: 'roadmap' | 'satellite') => {
        if (tileLayerRef.current) tileLayerRef.current.remove();
        const url = layer === 'roadmap' ? 'https://mt1.google.com/vt/lyrs=m&x={x}&y={y}&z={z}' : 'https://mt1.google.com/vt/lyrs=y&x={x}&y={y}&z={z}';
        tileLayerRef.current = L.tileLayer(url, { maxZoom: 20 }).addTo(map);
    };

    /**
     * Alle Marker neu zeichnen. `fit` = danach den Ausschnitt anpassen; beim
     * Neueinteilen der Buendel nach dem Zoomen bleibt die Ansicht stehen.
     */
    const updateMapMarkers = (map: L.Map, fit: boolean) => {
        // Offenes Projekt-Popup merken: Neuzeichnen ersetzt den Marker, das
        // Popup geht am neuen wieder auf. Sonst schloss der Klick auf einen
        // Marker (Auswahl -> Neuzeichnen) sein eigenes Popup, erst der zweite
        // Klick liess es offen.
        let openPopupProjectId: string | undefined;
        markerProjectIdsRef.current.forEach((id, m) => { if (m.isPopupOpen()) openPopupProjectId = id; });
        markerProjectIdsRef.current.clear();

        // Clear — alle Marker, auch die festen Orte (die stapelten sich
        // frueher bei jedem Neuzeichnen, die Suchmarke blieb nach dem Leeren).
        markersRef.current.forEach(m => m.remove());
        markersRef.current = [];
        if (routeLineRef.current) { routeLineRef.current.remove(); routeLineRef.current = null; }

        const bounds = L.latLngBounds([]);
        const addFixedMarker = (ort: GeoPunkt, icon: L.DivIcon, popupHtml: string, zIndexOffset = 0) => {
            const marker = L.marker([ort.lat, ort.lng], { icon, zIndexOffset }).addTo(map).bindPopup(popupHtml);
            markersRef.current.push(marker);
            bounds.extend([ort.lat, ort.lng]);
            return marker;
        };

        // 1. Base Locations
        if (userLocation) addFixedMarker(userLocation, createSpecialIcon('#3b82f6', 'Standort'), 'Ihr aktueller Standort');
        if (homeLocation) addFixedMarker(homeLocation, createSpecialIcon('#84cc16', 'Home', 'home'), esc(homeLocation.address));
        if (companyLocation) addFixedMarker(companyLocation, createSpecialIcon('#8b5cf6', 'Firma', 'building'), esc(companyLocation.address));
        if (searchLocation) {
            const marker = addFixedMarker(searchLocation, createSpecialIcon('#f97316', 'Suche', 'search'), esc(searchLocation.address), 1000);
            if (fit) marker.openPopup();
        }

        // 2. Project Markers
        const withCoords = displayedProjects.filter(p => p.lat && p.lng);
        let hasMarkers = false;
        withCoords.forEach(p => {
            if (!istGedimmt(p)) {
                bounds.extend([p.lat, p.lng]);
                hasMarkers = true;
            }
        });

        const zoom = map.getZoom();
        markerZoomRef.current = zoom;
        const single: typeof withCoords = [];
        if (selectedDay === 'all' && zoom < BUENDEL_BIS_ZOOM && withCoords.length > BUENDEL_AB_MARKERN) {
            // Das gewaehlte Projekt steht nie in einem Buendel.
            if (selectedProject?.lat && selectedProject?.lng) single.push(selectedProject);
            const candidates = withCoords.filter(p => p.id !== selectedProject?.id).map(p => {
                const pt = map.project([p.lat, p.lng], zoom);
                return { x: pt.x, y: pt.y, wert: p };
            });
            buendeln(candidates, BUENDEL_ZELLE_PX).forEach(group => {
                if (group.length === 1) single.push(group[0]);
                else addClusterMarker(map, group);
            });
        } else {
            single.push(...withCoords);
        }

        single.forEach(p => {
            // Day Filtering logic: if a specific day is selected, fade out others
            const isDimmed = istGedimmt(p);

            // Highlight Logic
            const isSelected = selectedProject?.id === p.id;
            const stopNumber = tourNummer.get(p.id);
            const due = faelligById.get(p.id) || null;

            const ownerInfo = projectOwners[p.user_id];
            const color = ownerInfo?.color || getFallbackColor(p.user_id);
            const meta: Partial<ReportMetadata> = p.data?.metadata || {};

            const marker = L.marker([p.lat, p.lng], {
                icon: createProjectIcon(color, isSelected, isDimmed, stopNumber, !!due && due.tage < 0),
                zIndexOffset: isSelected ? 1000 : (isDimmed ? 0 : 500)
            }).addTo(map);

            // --- HOVER TOOLTIP ---
            const tooltipContent = `
                <div class="font-sans text-xs min-w-[150px]">
                    ${stopNumber ? `<div class="font-bold text-blue-700 mb-1">Stopp ${stopNumber}</div>` : ''}
                    <div class="font-bold text-slate-800 text-sm mb-1">${esc(meta.projectName || p.name)}</div>
                    <div class="flex items-center gap-1 text-slate-500 mb-1">
                        <span class="font-mono text-[11px] bg-slate-100 px-1 rounded">#${esc(meta.projectNumber || '-')}</span>
                    </div>
                    <div class="text-slate-500 mb-1 truncate max-w-[200px]">${esc(meta.projectAddress || 'Keine Adresse')}</div>
                    ${due ? `<div class="${due.tage < 0 ? 'font-bold text-red-600' : 'text-slate-500'}">${esc(faelligText(due))}</div>` : ''}
                </div>
            `;

            marker.bindTooltip(tooltipContent, {
                direction: 'top',
                offset: [0, -28],
                opacity: 1,
                className: 'leaflet-tooltip-rounded'
            });

            // Click Handler
            marker.on('click', () => setSelectedProjectId(p.id));

            // Popup (Action)
            const popupContent = document.createElement('div');
            popupContent.innerHTML = `
                <div class="p-1 min-w-[150px]">
                    <h3 class="font-bold text-sm mb-1 text-slate-800">${esc(p.name)}</h3>
                    <button class="bg-blue-600 hover:bg-blue-700 text-white text-xs px-3 py-1.5 rounded w-full font-bold transition-colors">Öffnen</button>
                </div>
            `;
            popupContent.querySelector('button')?.addEventListener('click', () => openProjectById(p.id));

            marker.bindPopup(popupContent);
            markersRef.current.push(marker);
            markerProjectIdsRef.current.set(marker, p.id);
            if (p.id === openPopupProjectId) marker.openPopup();
        });

        // 3. Route: Start → Stopps in Tourreihenfolge (Luftlinie)
        if (tour && tour.stopps.length > 0) {
            const routePoints: L.LatLngExpression[] = [
                ...(tourStart ? [[tourStart.lat, tourStart.lng] as L.LatLngTuple] : []),
                ...tour.stopps.map(p => [p.lat, p.lng] as L.LatLngTuple),
            ];
            if (routePoints.length > 1) {
                routeLineRef.current = L.polyline(routePoints, { color: '#3b82f6', weight: 3, dashArray: '10, 10', opacity: 0.6 }).addTo(map);
            }
        }

        if (!fit) return;

        // Fit Bounds
        if (selectedProject?.lat && selectedProject?.lng) {
            map.setView([selectedProject.lat, selectedProject.lng], 15, { animate: true });
        } else if (hasMarkers || searchLocation || homeLocation || companyLocation) {
            try { map.fitBounds(bounds, { padding: [50, 50], maxZoom: 16 }); } catch {}
        } else if (userLocation) {
            map.setView([userLocation.lat, userLocation.lng], 12);
        }
    };

    const addClusterMarker = (map: L.Map, group: Array<GeoPunkt & { id: string; user_id?: string | null }>) => {
        const clusterBounds = L.latLngBounds(group.map(p => [p.lat, p.lng] as L.LatLngTuple));
        const colors = new Set(group.map(p => projectOwners[p.user_id || '']?.color || getFallbackColor(p.user_id || '')));
        const color = colors.size === 1 ? Array.from(colors)[0] : '#475569';
        const overdue = group.filter(p => (faelligById.get(p.id)?.tage ?? 0) < 0).length;

        const marker = L.marker(clusterBounds.getCenter(), { icon: createClusterIcon(group.length, color, overdue > 0), zIndexOffset: 400 }).addTo(map);
        marker.bindTooltip(
            `<div class="font-sans text-xs"><b>${group.length} Projekte</b>${overdue > 0 ? `<div class="text-red-600 font-bold">${overdue} überfällig</div>` : ''}<div class="text-slate-500">Klicken zum Hineinzoomen</div></div>`,
            { direction: 'top', offset: [0, -18], opacity: 1, className: 'leaflet-tooltip-rounded' },
        );
        marker.on('click', () => map.fitBounds(clusterBounds, { padding: [60, 60], maxZoom: BUENDEL_BIS_ZOOM }));
        markersRef.current.push(marker);
    };

    const createProjectIcon = (color: string, isPulsating: boolean, isDimmed: boolean, stopNumber?: number, isOverdue = false) => {
        const finalColor = isDimmed ? '#cbd5e1' : color; // Slate-300 for dimmed
        const opacity = isDimmed ? '0.5' : '1';
        const borderColor = isOverdue && !isDimmed ? '#dc2626' : 'white';

        let pulseEffect = '';
        if (isPulsating) {
            // Add a ring using box-shadow instead of complex animation for cleaner integration
            pulseEffect = `box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.4), 0 0 15px rgba(59, 130, 246, 0.6); transform: rotate(45deg) scale(1.2); z-index: 1000;`;
        } else {
             pulseEffect = `box-shadow: 0 2px 5px rgba(0,0,0,0.3); transform: rotate(45deg);`;
        }

        // Stoppnummer gegen die Drehung des Pins zurueckdrehen.
        const inner = stopNumber
            ? `<div style="transform: rotate(-45deg); color: white; font: 700 11px/1 sans-serif;">${stopNumber}</div>`
            : `<div style="width: 8px; height: 8px; background-color: white; border-radius: 50%;"></div>`;

        return L.divIcon({
            className: 'bg-transparent',
            html: `
                <div style="
                    background-color: ${esc(finalColor)};
                    width: 24px;
                    height: 24px;
                    border-radius: 50% 50% 0 50%;
                    border: 2px solid ${borderColor};
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    opacity: ${opacity};
                    ${pulseEffect}
                    transition: all 0.3s ease;
                ">
                    ${inner}
                </div>
            `,
            iconSize: [24, 24],
            iconAnchor: [12, 24],
            popupAnchor: [0, -24]
        });
    };

    const createClusterIcon = (count: number, color: string, hasOverdue: boolean) => L.divIcon({
        className: 'bg-transparent',
        html: `
            <div style="background-color: ${esc(color)}; width: 36px; height: 36px; border-radius: 50%; border: 3px solid ${hasOverdue ? '#dc2626' : 'white'}; box-shadow: 0 2px 6px rgba(0,0,0,0.35); display: flex; align-items: center; justify-content: center; color: white; font: 700 12px/1 sans-serif;">
                ${count}
            </div>
        `,
        iconSize: [36, 36],
        iconAnchor: [18, 18],
    });

    const createSpecialIcon = (color: string, label: string, type: 'dot' | 'home' | 'building' | 'search' = 'dot') => {
        let innerHtml = `<div style="width: 8px; height: 8px; background-color: white; border-radius: 50%;"></div>`;

        if (type === 'home') innerHtml = `<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>`;
        if (type === 'building') innerHtml = `<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><rect width="16" height="20" x="4" y="2" rx="2" ry="2"/><path d="M9 22v-4h6v4"/><path d="M8 6h.01"/><path d="M16 6h.01"/><path d="M8 10h.01"/><path d="M16 10h.01"/><path d="M8 14h.01"/><path d="M16 14h.01"/></svg>`;
        if (type === 'search') innerHtml = `<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/></svg>`;

        return L.divIcon({
            className: 'bg-transparent',
            html: `
                <div style="background-color: ${color}; width: 32px; height: 32px; border-radius: 50% 50% 0 50%; transform: rotate(45deg); border: 3px solid white; box-shadow: 0 4px 10px rgba(0,0,0,0.3); display: flex; align-items: center; justify-content: center;">
                    <div style="transform: rotate(-45deg);">${innerHtml}</div>
                </div>
            `,
            iconSize: [32, 32],
            iconAnchor: [16, 32],
            popupAnchor: [0, -32]
        });
    };

    const getCoordsMapUrl = (lat: number, lng: number) => {
        return `https://maps.google.com/maps?q=${lat},${lng}&t=&z=14&ie=UTF8&iwloc=&output=embed`;
    };

    // Toggle Owner in filter
    const toggleOwner = (id: string) => {
        const newSet = new Set(hiddenOwnerIds);
        if (newSet.has(id)) newSet.delete(id);
        else newSet.add(id);
        setHiddenOwnerIds(newSet);
    };
    const isOwnerFilterActive = ownerOptions.some(o => hiddenOwnerIds.has(o.id));

    const selectedDayName = selectedDay === 'all' ? '' : WEEKDAYS.find(d => d.id === selectedDay)?.long || '';

    // Navigationsknoepfe der Tagestour — in der Tour-Leiste der Liste UND auf
    // der Karte, damit sie nicht mit der Liste aus dem Bild scrollen.
    const renderRouteButtons = () => tourLinks.map((url, i) => (
        <button
            key={url}
            onClick={() => externOeffnen(url)}
            className="bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold px-3 py-1.5 rounded-lg flex items-center gap-1.5"
        >
            <Navigation className="w-3 h-3" />
            {tourLinks.length > 1 ? `Route Teil ${i + 1}` : 'Route in Google Maps'}
        </button>
    ));

    // --- RENDER SIDEBAR LIST (GROUPED) ---
    const renderSidebarList = () => {
        // `groupDay`: in welcher Tagesgruppe der Eintrag steht. Die Stoppnummer
        // gehoert nur in die Gruppe des gewaehlten Tags — ein Projekt mit Mo+Mi
        // steht sonst in der Mittwochsgruppe mit der Montagsnummer.
        const renderItem = (p: KartenProjekt, groupDay: number | null) => {
            const meta = p.data.metadata as ReportMetadata;
            const isSelected = selectedProject?.id === p.id;
            const hasCoords = !!p.lat && !!p.lng;
            const ownerInfo = projectOwners[p.user_id];
            const userColor = ownerInfo?.color || getFallbackColor(p.user_id);
            const isDimmed = istGedimmt(p);
            const stopNumber = groupDay !== null && groupDay === selectedDay ? tourNummer.get(p.id) : undefined;
            const due = faelligById.get(p.id) || null;

            return (
                <div
                    key={p.id}
                    onClick={() => setSelectedProjectId(p.id)}
                    className={`p-4 cursor-pointer transition-all border-b border-slate-100 last:border-0
                        ${isSelected ? 'bg-blue-50 border-l-4 border-l-blue-600' : 'border-l-4 border-l-transparent hover:bg-slate-50'}
                        ${isDimmed ? 'opacity-40 grayscale-[0.8]' : ''}
                    `}
                >
                    <div className="flex justify-between items-start gap-2 mb-1">
                        <h4 className={`font-bold text-sm flex items-center gap-2 min-w-0 ${isSelected ? 'text-blue-800' : 'text-slate-800'}`}>
                            {stopNumber && <span className="shrink-0 w-5 h-5 rounded-full bg-blue-600 text-white text-[11px] font-bold flex items-center justify-center">{stopNumber}</span>}
                            <span className="truncate">{p.name || 'Unbenannt'}</span>
                        </h4>
                        {typeof p.distance === 'number' && <span className="shrink-0 text-[11px] font-bold bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded">{p.distance.toFixed(1)} km</span>}
                        {viewMode === 'all' && hasCoords && typeof p.distance !== 'number' && <MapPin className="shrink-0 w-3 h-3 text-blue-500 fill-blue-500" />}
                        {!hasCoords && (
                            <span
                                title="Keine Koordinaten — das Projekt erscheint nicht auf der Karte. Adresse in den Projekt-Stammdaten verorten."
                                className="shrink-0 flex items-center gap-1 text-[11px] font-bold text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded"
                            >
                                <MapPinOff className="w-3 h-3" /> ohne Koordinaten
                            </span>
                        )}
                    </div>
                    <div className="flex items-center gap-2 text-xs text-slate-500 mb-2">
                        <MapPin className="w-3 h-3 shrink-0" />
                        <span className="truncate">{meta.projectAddress || 'Keine Adresse'}</span>
                    </div>
                    <div className="flex justify-between items-center gap-2 mt-3">
                        <div className="flex items-center gap-2 min-w-0">
                            <div className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: userColor }}></div>
                            <span className="text-[11px] text-slate-600 truncate max-w-[100px]">{ownerInfo?.name || meta.coordinator || 'Unbekannt'}</span>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                            {due && <span className={`text-[11px] font-bold px-1.5 py-0.5 rounded ${faelligKlasse(due)}`}>{faelligText(due)}</span>}
                            {isSelected && <button onClick={(e) => { e.stopPropagation(); openProjectById(p.id); }} className="text-[11px] font-bold text-blue-600 flex items-center hover:underline">Öffnen <ArrowRight className="w-3 h-3 ml-1" /></button>}
                        </div>
                    </div>
                </div>
            );
        };

        // Always render Grouped List to show headers
        return (
            <div className="divide-y divide-slate-100">
                {WEEKDAYS.map(day => {
                    const items = groupedProjects[day.id];
                    if (!items || items.length === 0) return null;
                    const isDaySelected = selectedDay === day.id;
                    // Am gewaehlten Tag in Tourreihenfolge; ohne Koordinaten ans Ende.
                    const ordered = isDaySelected
                        ? [...items].sort((a, b) => (tourNummer.get(a.id) ?? 1e9) - (tourNummer.get(b.id) ?? 1e9))
                        : items;

                    return (
                        <div key={day.id} className="bg-white">
                            <div
                                className={`px-4 py-2 text-xs font-bold uppercase tracking-wider border-y border-slate-200 sticky top-0 z-10 flex justify-between cursor-pointer transition-colors hover:bg-slate-100 ${isDaySelected ? 'bg-blue-600 text-white hover:bg-blue-700' : 'bg-slate-50 text-slate-500'}`}
                                onClick={() => setSelectedDay(isDaySelected ? 'all' : day.id)}
                            >
                                <span className="flex items-center gap-2">
                                    <MousePointer2 className="w-3 h-3" />
                                    {day.long}
                                </span>
                                <span className={`px-2 rounded-full border ${isDaySelected ? 'bg-white/20 border-white/30' : 'bg-white border-slate-200'}`}>
                                    {items.length}
                                </span>
                            </div>
                            <div>
                                {ordered.map(p => renderItem(p, day.id))}
                            </div>
                        </div>
                    );
                })}
                {/* Unassigned */}
                {groupedProjects['none'] && groupedProjects['none'].length > 0 && (
                    <div className="bg-white">
                        <div className="bg-slate-100 px-4 py-2 text-xs font-bold text-slate-400 uppercase tracking-wider border-y border-slate-200 sticky top-0 z-10 flex justify-between">
                            <span>Nicht zugewiesen / Flexibel</span>
                            <span className="bg-white px-2 rounded-full border border-slate-200">{groupedProjects['none'].length}</span>
                        </div>
                        <div>
                            {groupedProjects['none'].map(p => renderItem(p, null))}
                        </div>
                    </div>
                )}
            </div>
        );
    };

    return (
        <div data-glass-view="karte" className="flex flex-col h-full bg-slate-50 animate-fade-in relative">

            {/* --- HEADER --- (auf dem Handy kompakt: jede Zeile hier fehlt der Karte) */}
            <header className="bg-white border-b border-slate-200 p-3 md:p-4 shrink-0 flex flex-col xl:flex-row justify-between items-start xl:items-center gap-2 md:gap-4 z-20 shadow-sm">
                <div>
                    <h1 className="text-lg md:text-xl font-bold text-slate-800 flex items-center gap-2">
                        <MapIcon className="w-5 h-5 md:w-6 md:h-6 text-blue-600" />
                        Tourenplanung & Karte
                    </h1>
                    <div className="hidden md:flex text-xs text-slate-500 gap-2 mt-1">
                        {userLocation && <span className="flex items-center gap-1 text-blue-600"><Navigation className="w-3 h-3"/> Standort aktiv</span>}
                        {companyLocation && <span className="flex items-center gap-1 text-purple-600"><Building2 className="w-3 h-3"/> Firma gefunden</span>}
                        {homeLocation && <span className="flex items-center gap-1 text-green-600"><Home className="w-3 h-3"/> Zuhause gefunden</span>}
                    </div>
                </div>

                <div className="flex flex-wrap gap-2 md:gap-3 items-center w-full xl:w-auto md:justify-end">
                    <div data-glass="umschalter" className="flex bg-slate-100 rounded-lg p-1 border border-slate-200">
                        <button onClick={() => setViewMode('single')} title="Detail" className={`px-3 py-1.5 rounded-md text-xs font-bold transition-all flex items-center gap-2 ${viewMode === 'single' ? 'bg-white shadow text-blue-600' : 'text-slate-500 hover:text-slate-700'}`}><LayoutGrid className="w-3 h-3"/><span className="hidden sm:inline">Detail</span></button>
                        <button onClick={() => setViewMode('all')} title="Karte" className={`px-3 py-1.5 rounded-md text-xs font-bold transition-all flex items-center gap-2 ${viewMode === 'all' ? 'bg-white shadow text-blue-600' : 'text-slate-500 hover:text-slate-700'}`}><Globe className="w-3 h-3"/><span className="hidden sm:inline">Karte</span></button>
                    </div>

                    <div data-glass="umschalter" className="flex bg-slate-100 rounded-lg p-1 border border-slate-200 gap-1">
                        <button
                            onClick={() => setHideFinished(!hideFinished)}
                            title="Projekte mit Status Beendet oder Archiv ausblenden"
                            className={`px-3 py-1.5 rounded-md text-xs font-bold transition-all flex items-center gap-1.5 ${hideFinished ? 'bg-white shadow text-blue-600' : 'text-slate-500 hover:text-slate-700'}`}
                        >
                            {hideFinished && <Check className="w-3 h-3"/>}
                            <span className="sm:hidden">Beendete aus</span>
                            <span className="hidden sm:inline">Beendete ausblenden</span>
                        </button>
                        <button
                            onClick={() => setOnlyNoInspection(!onlyNoInspection)}
                            title="Nur Projekte zeigen, an denen noch keine Begehung durchgeführt wurde (V0). SiFa-Betreuung und berichtsbefreite Projekte zählen nicht."
                            className={`px-3 py-1.5 rounded-md text-xs font-bold transition-all flex items-center gap-1.5 ${onlyNoInspection ? 'bg-white shadow text-blue-600' : 'text-slate-500 hover:text-slate-700'}`}
                        >
                            {onlyNoInspection && <Check className="w-3 h-3"/>}
                            <span className="sm:hidden">Ohne Begehung</span>
                            <span className="hidden sm:inline">Nur ohne Begehung</span>
                        </button>
                    </div>

                    {darfAlle && (
                        <div data-glass="umschalter" className="flex bg-slate-100 rounded-lg p-1 border border-slate-200">
                             <button onClick={() => setShowOnlyMine(!showOnlyMine)} className={`px-3 py-1.5 rounded-md text-xs font-bold transition-all flex items-center gap-1.5 ${showOnlyMine ? 'bg-white shadow text-blue-600' : 'text-slate-500 hover:text-slate-700'}`}>
                                 {showOnlyMine ? 'Meine' : 'Alle'}
                                 {alleLaedt && <Loader2 className="w-3 h-3 animate-spin" aria-label="lädt" />}
                             </button>
                        </div>
                    )}
                </div>
            </header>

            {/* --- MAIN CONTENT --- */}
            <div data-glass="inhalt" className="flex-1 flex flex-col md:flex-row min-h-0 overflow-hidden relative">

                {/* SIDEBAR: LIST — auf dem Handy hoechstens 40 % der Hoehe, sonst bleibt fuer die Karte nichts */}
                <div data-glass="liste" className="w-full md:w-80 lg:w-96 max-h-[40vh] md:max-h-none bg-white border-r border-slate-200 flex flex-col z-10 shadow-lg shrink-0">
                    <div className="p-3 md:p-4 border-b border-slate-100 space-y-2 md:space-y-3 bg-slate-50/50">
                        <div className="flex gap-2">
                            <div className="relative flex-1">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                                <input
                                    type="text"
                                    placeholder="Adresse oder Projekt suchen..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    onKeyDown={(e) => e.key === 'Enter' && performGlobalSearch()}
                                    className="w-full pl-9 pr-8 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none bg-white"
                                />
                                {searchTerm && <button onClick={() => { setSearchTerm(''); setSearchLocation(null); }} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"><X className="w-4 h-4" /></button>}
                            </div>

                            {/* USER FILTER BUTTON (nur unter „Alle") */}
                            {zeigeAlle && (
                                <div className="relative">
                                    <Button
                                        onClick={() => setIsOwnerFilterOpen(!isOwnerFilterOpen)}
                                        className={`px-3 h-full ${isOwnerFilterActive ? 'bg-blue-600 text-white' : 'bg-white border border-slate-300 text-slate-600'}`}
                                    >
                                        <Users className="w-4 h-4" />
                                    </Button>

                                    {isOwnerFilterOpen && (
                                        <div className="absolute top-full right-0 mt-2 w-64 bg-white rounded-xl shadow-2xl border border-slate-200 z-[600] overflow-hidden animate-in fade-in zoom-in-95 duration-200">
                                            <div className="p-3 border-b border-slate-100 bg-slate-50 flex justify-between items-center">
                                                <span className="text-xs font-bold text-slate-500 uppercase">Benutzer Filter</span>
                                                <button onClick={() => setIsOwnerFilterOpen(false)}><X className="w-4 h-4 text-slate-400"/></button>
                                            </div>
                                            <div className="max-h-60 overflow-y-auto p-2">
                                                {ownerOptions.map(owner => {
                                                    const isVisible = !hiddenOwnerIds.has(owner.id);
                                                    return (
                                                        <div
                                                            key={owner.id || '-'}
                                                            onClick={() => toggleOwner(owner.id)}
                                                            className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer hover:bg-slate-50 transition-colors`}
                                                        >
                                                            <div className={`w-4 h-4 rounded border flex items-center justify-center ${isVisible ? 'bg-blue-600 border-blue-600 text-white' : 'border-slate-300'}`}>
                                                                {isVisible && <Check className="w-3 h-3" />}
                                                            </div>
                                                            <div className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: owner.color }}></div>
                                                            <span className="text-sm font-medium text-slate-700 truncate">{owner.name}</span>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                            <div className="p-2 border-t border-slate-100 bg-slate-50 flex justify-between">
                                                <button onClick={() => setHiddenOwnerIds(new Set())} className="text-[11px] font-bold text-blue-600 hover:underline">Alle an</button>
                                                <button onClick={() => setHiddenOwnerIds(new Set(ownerOptions.map(o => o.id)))} className="text-[11px] font-bold text-slate-500 hover:underline">Alle aus</button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}

                            <Button onClick={() => performGlobalSearch()} className="px-3"><Search className="w-4 h-4" /></Button>
                        </div>
                        {isSearchingLocation && <div className="text-[11px] text-blue-600 flex items-center"><Loader2 className="w-3 h-3 animate-spin mr-1"/> Suche Ort...</div>}
                        {(ausgeblendet.length > 0 || ohneKoordinaten > 0) && (
                            <div className="text-[11px] text-slate-500 flex flex-wrap items-center gap-x-3 gap-y-1">
                                {ausgeblendet.length > 0 && (
                                    <span>
                                        Ausgeblendet: {ausgeblendet.join(' · ')}
                                        <button onClick={() => { setHideFinished(false); setOnlyNoInspection(false); }} className="ml-1.5 font-bold text-blue-600 hover:underline">einblenden</button>
                                    </span>
                                )}
                                {ohneKoordinaten > 0 && (
                                    <span className="flex items-center gap-1 text-amber-700" title="Diese Projekte stehen in der Liste, aber nicht auf der Karte und nicht in der Route.">
                                        <MapPinOff className="w-3 h-3" /> {ohneKoordinaten} ohne Koordinaten
                                    </span>
                                )}
                            </div>
                        )}
                    </div>

                    <div ref={listScrollRef} className="flex-1 overflow-y-auto">
                        {/* TAGESTOUR — im Scrollbereich: auf dem Handy nimmt sie der Liste sonst festen Platz */}
                        {selectedDay !== 'all' && (
                            <div className="p-3 border-b border-blue-100 bg-blue-50/60 space-y-2">
                                <div className="flex items-center justify-between gap-2">
                                    <span className="text-sm font-bold text-blue-800 flex items-center gap-2"><RouteIcon className="w-4 h-4" /> Tour {selectedDayName}</span>
                                    <button onClick={() => setSelectedDay('all')} title="Tour schließen" className="text-slate-400 hover:text-slate-600"><X className="w-4 h-4" /></button>
                                </div>
                                {tour && tour.stopps.length > 0 ? (
                                    <>
                                        <div className="text-xs text-slate-600">
                                            {tour.stopps.length} {tour.stopps.length === 1 ? 'Stopp' : 'Stopps'} · {tour.km.toFixed(0)} km Luftlinie
                                            {!tourStart && ' · ohne Startpunkt'}
                                        </div>
                                        {startOptionen.length > 0 && (
                                            <label className="flex items-center gap-2 text-xs text-slate-600">
                                                Start:
                                                <select
                                                    value={aktiverStart?.id}
                                                    onChange={(e) => setRouteStart(e.target.value as RouteStart)}
                                                    className="border border-slate-300 rounded px-2 py-1 bg-white text-xs"
                                                >
                                                    {startOptionen.map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
                                                </select>
                                            </label>
                                        )}
                                        <div className="flex flex-wrap gap-2">
                                            {renderRouteButtons()}
                                        </div>
                                        {tourLinks.length > 1 && (
                                            <div className="text-[11px] text-slate-500">Google Maps nimmt höchstens 9 Zwischenziele — die Tour ist in Teilstrecken geteilt.</div>
                                        )}
                                    </>
                                ) : (
                                    <div className="text-xs text-slate-500">Keine Stopps mit Koordinaten an diesem Tag.</div>
                                )}
                            </div>
                        )}
                        {displayedProjects.length === 0 ? (
                            <div className="p-8 text-center text-slate-400 text-sm space-y-1">
                                <div>Keine Projekte gefunden.</div>
                                {(hideFinished || onlyNoInspection || searchTerm || searchLocation) && (
                                    <div className="text-xs">Filter oder Suche aktiv — oben anpassen.</div>
                                )}
                            </div>
                        ) : (
                            renderSidebarList()
                        )}
                    </div>
                </div>

                {/* MAP AREA */}
                <div data-glass="karte-flaeche" className="flex-1 bg-slate-200 relative min-h-0">
                    {viewMode === 'all' && (
                        // Vollbild: derselbe Baum, nur fixiert ueber der App (unter Dialogen,
                        // z-[160]/[200]; ueber der Handy-Leiste, z-[90]/[91]) — der
                        // Kartencontainer bleibt dasselbe Element, Leaflet bleibt bestehen.
                        <div
                            className={isMapFullscreen ? 'fixed inset-0 z-[150] bg-slate-200' : 'w-full h-full'}
                            style={isMapFullscreen ? { paddingTop: 'env(safe-area-inset-top)', paddingBottom: 'env(safe-area-inset-bottom)' } : undefined}
                        >
                        <div className="w-full h-full relative">
                            {/* Map Layer Toggle + Vollbild */}
                            <div className="absolute top-4 right-4 z-[500] bg-white rounded-lg shadow-lg border border-slate-200 p-1 flex flex-col gap-1">
                                <button onClick={() => setIsMapFullscreen(!isMapFullscreen)} className="p-2 rounded hover:bg-slate-100 text-slate-600" title={isMapFullscreen ? 'Vollbild verlassen' : 'Karte im Vollbild'}>
                                    {isMapFullscreen ? <Minimize2 className="w-5 h-5" /> : <Maximize2 className="w-5 h-5" />}
                                </button>
                                <button onClick={() => setMapLayer('roadmap')} className={`p-2 rounded hover:bg-slate-100 ${mapLayer === 'roadmap' ? 'text-blue-600 bg-blue-50' : 'text-slate-600'}`} title="Straßenkarte"><MapIcon className="w-5 h-5" /></button>
                                <button onClick={() => setMapLayer('satellite')} className={`p-2 rounded hover:bg-slate-100 ${mapLayer === 'satellite' ? 'text-blue-600 bg-blue-50' : 'text-slate-600'}`} title="Satellit"><Layers className="w-5 h-5" /></button>
                            </div>

                            {/* Selected Day Indicator + Navigation */}
                            {selectedDay !== 'all' && (
                                <div className="absolute top-4 left-4 right-20 md:right-auto md:max-w-sm z-[500] bg-white/95 backdrop-blur px-3 py-2 rounded-lg shadow-lg border-l-4 border-blue-600 space-y-2">
                                    <div className="flex items-center gap-2 font-bold text-blue-800 text-sm">
                                        <Calendar className="w-4 h-4 shrink-0" />
                                        <span className="truncate">
                                            {selectedDayName}
                                            {tour && tour.stopps.length > 0 ? ` · ${tour.stopps.length} ${tour.stopps.length === 1 ? 'Stopp' : 'Stopps'} · ${tour.km.toFixed(0)} km` : ''}
                                        </span>
                                        <button onClick={() => setSelectedDay('all')} title="Tour schließen" className="ml-auto shrink-0 hover:bg-slate-200 rounded-full p-1"><X className="w-4 h-4"/></button>
                                    </div>
                                    {tourLinks.length > 0 && <div className="flex flex-wrap gap-2">{renderRouteButtons()}</div>}
                                </div>
                            )}

                            <div ref={mapContainerRef} className="w-full h-full z-0" />
                        </div>
                        </div>
                    )}

                    {viewMode === 'single' && (
                        <div className="w-full h-full flex items-center justify-center bg-slate-100">
                             {selectedProject ? (
                                selectedProject.lat && selectedProject.lng ? (
                                     <iframe width="100%" height="100%" src={getCoordsMapUrl(selectedProject.lat, selectedProject.lng)} style={{ border: 0 }} allowFullScreen loading="lazy" title="Map" />
                                ) : (
                                    <div className="text-center text-slate-500"><MapPin className="w-16 h-16 mb-4 opacity-20 mx-auto"/><p>Projekt hat keine Koordinaten.</p></div>
                                )
                             ) : (
                                <div className="text-center text-slate-500"><MapIcon className="w-16 h-16 mb-4 opacity-20 mx-auto"/><p>Wähle ein Projekt aus der Liste.</p></div>
                             )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};
